import { useEffect, useRef, useState } from 'react';

import type { EpisodePipelineState, StepStatus } from '../hooks/useBulkEpisodeOrchestrator';

interface BulkEpisodePipelineTrackerProps {
  episodes: EpisodePipelineState[];
}

const STEP_LABELS: Record<string, string> = {
  generate: 'Generate',
  save: 'Save',
  localize: 'Localize',
  voices: 'Voices',
};

function StepCell({ status, error }: { status: StepStatus; error?: string }) {
  switch (status) {
    case 'ok':
      return (
        <span className="inline-block rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
          OK
        </span>
      );
    case 'fail':
      return (
        <span
          className="inline-block cursor-help rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800"
          title={error || 'Failed'}
        >
          FAIL
        </span>
      );
    case 'in_progress':
      return (
        <span className="inline-flex items-center gap-1 rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-blue-500" />
          ...
        </span>
      );
    case 'pending':
    default:
      return <span className="text-xs text-gray-300">&mdash;</span>;
  }
}

function isRowActive(ep: EpisodePipelineState): boolean {
  return Object.values(ep.steps).some((s) => s === 'in_progress');
}

function isRowCompleted(ep: EpisodePipelineState): boolean {
  return Object.values(ep.steps).every((s) => s === 'ok' || s === 'fail');
}

export function BulkEpisodePipelineTracker({ episodes }: BulkEpisodePipelineTrackerProps) {
  const [showAll, setShowAll] = useState(false);
  const activeRef = useRef<HTMLTableRowElement>(null);

  const firstActiveIndex = episodes.findIndex(isRowActive);

  // Auto-scroll to first active row — only when active row changes
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [firstActiveIndex]);

  const visibleEpisodes = showAll
    ? episodes
    : episodes.filter((ep) => !isRowCompleted(ep) || isRowActive(ep));

  const completedHidden = episodes.length - visibleEpisodes.length;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Episode Pipeline Tracker</h3>
        {completedHidden > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-xs text-indigo-600 hover:text-indigo-800"
          >
            {showAll ? 'Hide completed' : `Show all (${completedHidden} completed hidden)`}
          </button>
        )}
      </div>

      <div className="max-h-[400px] overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 w-10">#</th>
              <th className="px-3 py-2">Idea</th>
              <th className="px-3 py-2 w-14 text-center">Level</th>
              <th className="px-3 py-2 w-16 text-center">Duration</th>
              {Object.keys(STEP_LABELS).map((step) => (
                <th key={step} className="px-3 py-2 w-24 text-center">
                  {STEP_LABELS[step]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibleEpisodes.map((ep) => {
              const active = isRowActive(ep);
              return (
                <tr
                  key={ep.index}
                  ref={ep.index === firstActiveIndex ? activeRef : undefined}
                  className={active ? 'bg-blue-50' : ''}
                >
                  <td className="px-3 py-2 text-gray-400">{ep.index + 1}</td>
                  <td className="px-3 py-2">
                    <div className="max-w-xs truncate" title={ep.idea}>
                      {ep.idea.slice(0, 60)}
                      {ep.idea.length > 60 && '...'}
                    </div>
                    {ep.episodeId && (
                      <div className="mt-0.5 text-xs text-gray-400 font-mono">
                        {ep.episodeId.slice(0, 8)}...
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-gray-500">
                    {ep.skillLevel ?? ''}
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-gray-500">
                    {ep.duration ?? ''}
                  </td>
                  {(Object.keys(STEP_LABELS) as (keyof typeof ep.steps)[]).map((step) => (
                    <td key={step} className="px-3 py-2 text-center">
                      <StepCell
                        status={ep.steps[step]}
                        error={ep.errorStep === step ? ep.error : undefined}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-xs text-gray-400">
        Legend: OK = done, ... = in progress, &mdash; = waiting, FAIL = failed (hover for error)
      </div>
    </div>
  );
}
